import axios from 'axios'

import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('gas_estimation.api')

// Transaction types that can be estimated
export enum TransactionType {
    WRAP = 'wrap',
    APPROVE = 'approve',
    PLACE_ORDER = 'placeOrder',
    TRANSFER = 'transfer',
    DEPOSIT_FOR_BURN = 'depositForBurn',
}

// Gas fee metrics structure
export interface GasFeeMetrics {
    chainId: number
    isEIP1559: boolean
    currentGasPrice: bigint
    EIP1559Params?: {
        baseFee: bigint
        priorityFee: bigint
        maxFee: bigint
    }
    isTron?: boolean
    TronParams?: {
        energyPrice: bigint
        bandwidthPrice: bigint
    }
    isSolana?: boolean
    SolanaParams?: {
        feePerSignature: bigint
        recentBlockhash: string
        minimumRentExemption?: bigint
    }
}

// Transaction fee results structure (replaces GasFeeSimulationResults)
export interface TransactionFeeResults {
    /**
     * The cost of the transaction in the native gas token (e.g., ETH for Ethereum).
     */
    costInGasToken: bigint

    /**
     * The cost of the transaction in USD.
     * If the token price is not available, this value will be null.
     */
    costInUSD: number | null

    /**
     * Optional error that occurred during simulation.
     */
    simulationError?: {
        message: string
        transactionType?: TransactionType
        chainId?: number
        [key: string]: any
    }
}

// Structure of the gas estimation results for each chain
export interface GasEstimationResults {
    /**
     * The current gas metrics fetched from a gas price API.
     */
    feeMetrics: GasFeeMetrics

    /**
     * Maximum multiplier basis points for the gas price.
     * This is a chain-specific value between 10,000 and 200,000.
     * 10000 = 1x multiplier
     */
    setMaxMultiplierBasisPoints: number

    /**
     * The results of gas estimation for each transaction type.
     */
    results: Record<TransactionType, TransactionFeeResults>
}

// Structure of the response data - a record of chain IDs to gas estimation results
export interface GasEstimationData {
    [chainId: string]: GasEstimationResults
}

// Full API response structure
export interface GasEstimationResponse {
    data: GasEstimationData
    success: boolean
    timestamp: number
    last_updated: string
}

// Error response structure
export interface GasEstimationErrorResponse {
    success: false
    error: string
}

/**
 * Fetches gas fee estimates from the Gas Estimation Worker
 * @returns Promise with gas fee estimates data
 */
export async function apiGetGasEstimates(): Promise<GasEstimationResponse> {
    try {
        const workerUrl = process.env.REACT_APP_GAS_ESTIMATION_URL

        if (!workerUrl) {
            throw new Error('Gas estimation worker URL is not configured')
        }

        // Normalize the URL to ensure it ends with a slash
        const baseUrl = workerUrl.endsWith('/') ? workerUrl : `${workerUrl}/`
        const url = `${baseUrl}gas_fee_estimates`

        // logger.info(`Fetching gas fee estimates from: ${url}`)

        const response = await axios.get<GasEstimationResponse>(url)

        // Convert string BigInts back to native BigInts
        const data = response.data

        // logger.success('Gas fee estimates fetched successfully.\nResults:', data)

        // Process the data to convert string representations of BigInts back to actual BigInts
        Object.keys(data.data).forEach(chainId => {
            const chainData = data.data[chainId]

            // Convert fee metrics
            if (typeof chainData.feeMetrics.currentGasPrice === 'string') {
                chainData.feeMetrics.currentGasPrice = BigInt(chainData.feeMetrics.currentGasPrice)
            }

            if (chainData.feeMetrics.EIP1559Params) {
                if (typeof chainData.feeMetrics.EIP1559Params.baseFee === 'string') {
                    chainData.feeMetrics.EIP1559Params.baseFee = BigInt(chainData.feeMetrics.EIP1559Params.baseFee)
                }
                if (typeof chainData.feeMetrics.EIP1559Params.priorityFee === 'string') {
                    chainData.feeMetrics.EIP1559Params.priorityFee = BigInt(
                        chainData.feeMetrics.EIP1559Params.priorityFee
                    )
                }
                if (typeof chainData.feeMetrics.EIP1559Params.maxFee === 'string') {
                    chainData.feeMetrics.EIP1559Params.maxFee = BigInt(chainData.feeMetrics.EIP1559Params.maxFee)
                }
            }

            // Convert other chain-specific parameters if they exist
            if (chainData.feeMetrics.SolanaParams) {
                if (typeof chainData.feeMetrics.SolanaParams.feePerSignature === 'string') {
                    chainData.feeMetrics.SolanaParams.feePerSignature = BigInt(
                        chainData.feeMetrics.SolanaParams.feePerSignature
                    )
                }
                if (typeof chainData.feeMetrics.SolanaParams.minimumRentExemption === 'string') {
                    chainData.feeMetrics.SolanaParams.minimumRentExemption = BigInt(
                        chainData.feeMetrics.SolanaParams.minimumRentExemption
                    )
                }
            }

            if (chainData.feeMetrics.TronParams) {
                if (typeof chainData.feeMetrics.TronParams.energyPrice === 'string') {
                    chainData.feeMetrics.TronParams.energyPrice = BigInt(chainData.feeMetrics.TronParams.energyPrice)
                }
                if (typeof chainData.feeMetrics.TronParams.bandwidthPrice === 'string') {
                    chainData.feeMetrics.TronParams.bandwidthPrice = BigInt(
                        chainData.feeMetrics.TronParams.bandwidthPrice
                    )
                }
            }

            // Convert results for each transaction type
            Object.keys(chainData.results).forEach(txType => {
                const result = chainData.results[txType as TransactionType]

                if (typeof result.costInGasToken === 'string') {
                    result.costInGasToken = BigInt(result.costInGasToken)
                }

                // costInUSD is already a number or null, no conversion needed
            })
        })

        return data
    } catch (error) {
        logger.error('Error fetching gas fee estimates:', error)
        throw error
    }
}
